import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import toast from '../lib/toast';
import { patientsService } from '../adapters/patients';
import { Patient } from '../types';

interface RecentPatient {
	patient_id: string;
	first_name: string;
	last_name: string;
}

interface PatientsContextValue {
	patients: Patient[];
	nextCursor: string | null;
	loading: boolean;
	initialLoaded: boolean;
	fetchInitial: () => Promise<void>;
	loadMore: () => Promise<void>;
	addPatient: (patient: Patient) => void;
	updateCachedPatient: (patient: Patient) => void;
	recentPatients: RecentPatient[];
	recordRecent: (patient: Patient) => void;
	savePatient: (id: string, payload: Partial<Patient>) => Promise<Patient>;
	deactivatePatient: (id: string) => Promise<void>;
}

const PatientsContext = createContext<PatientsContextValue | null>(null);

export const usePatients = (): PatientsContextValue => {
	const ctx = useContext(PatientsContext);
	if (!ctx) throw new Error('usePatients must be used within PatientsProvider');
	return ctx;
};

export const PatientsProvider = ({ children }: { children: ReactNode }) => {
	const [patients, setPatients] = useState<Patient[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [initialLoaded, setInitialLoaded] = useState(false);
	const [recentPatients, setRecentPatients] = useState<RecentPatient[]>([]);

	useEffect(() => {
		try {
			const raw = localStorage.getItem('recent_patients');
			if (raw) {
				const parsed = JSON.parse(raw);
				if (Array.isArray(parsed)) setRecentPatients(parsed.slice(0, 6));
			}
		} catch (_) {}
	}, []);

	const fetchInitial = useCallback(async () => {
		if (loading) return;
		setLoading(true);
		try {
			const data = await patientsService.listPatients(25);
			setPatients(data.items || []);
			setNextCursor(data.next_cursor || null);
			setInitialLoaded(true);
		} catch (e: any) {
			toast.error(e.response?.data?.msg || 'Failed to load patients');
		} finally {
			setLoading(false);
		}
	}, [loading]);

	const loadMore = useCallback(async () => {
		if (loading || !nextCursor) return;
		setLoading(true);
		try {
			const data = await patientsService.listPatients(25, nextCursor);
			setPatients((prev) => [...prev, ...(data.items || [])]);
			setNextCursor(data.next_cursor || null);
		} catch (e: any) {
			toast.error(e.response?.data?.msg || 'Failed to load more patients');
		} finally {
			setLoading(false);
		}
	}, [loading, nextCursor]);

	const addPatient = useCallback((patient: Patient) => {
		setPatients((prev) => [patient, ...prev]);
	}, []);

	const updateCachedPatient = useCallback((patient: Patient) => {
		setPatients((prev) => prev.map((p) => (p.patient_id === patient.patient_id ? patient : p)));
	}, []);

	const recordRecent = useCallback((patient: Patient) => {
		if (!patient?.patient_id) return;
		setRecentPatients((prev) => {
			const existing = prev.filter((p) => p.patient_id !== patient.patient_id);
			const updated = [
				{ patient_id: patient.patient_id, first_name: patient.first_name, last_name: patient.last_name },
				...existing,
			].slice(0, 6);
			try {
				localStorage.setItem('recent_patients', JSON.stringify(updated));
			} catch (_) {}
			return updated;
		});
	}, []);

	const savePatient = useCallback(async (id: string, payload: Partial<Patient>) => {
		const updated = await patientsService.updatePatient(id, payload);
		setPatients((prev) => prev.map((p) => (p.patient_id === id ? updated : p)));
		return updated;
	}, []);

	const deactivatePatient = useCallback(async (id: string) => {
		await patientsService.deactivatePatient(id);
		setPatients((prev) => prev.filter((p) => p.patient_id !== id));
		setRecentPatients((prev) => {
			const updated = prev.filter((p) => p.patient_id !== id);
			try {
				localStorage.setItem('recent_patients', JSON.stringify(updated));
			} catch (_) {}
			return updated;
		});
	}, []);

	return (
		<PatientsContext.Provider
			value={{
				patients,
				nextCursor,
				loading,
				initialLoaded,
				fetchInitial,
				loadMore,
				addPatient,
				updateCachedPatient,
				recentPatients,
				recordRecent,
				savePatient,
				deactivatePatient,
			}}
		>
			{children}
		</PatientsContext.Provider>
	);
};
